import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '../lib/supabase';
import {
  getToken, setToken as storeToken, removeToken,
  getCachedUser, setCachedUser, removeCachedUser,
  setPreference, removePreference, getPreference,
  clearAuthSession,
} from '../lib/tokenStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize from cache immediately — no blocking API call needed for shell render
  const storedUser = () => getCachedUser();
  const [user, setUser] = useState(storedUser);
  const [token, setToken] = useState(getToken());
  const [loading, setLoading] = useState(!storedUser());

  useEffect(() => {
    const checkUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          setCachedUser(response.data);
        } catch (error) {
          // Only clear token if it's not a network error (user might be offline temporarily)
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            clearAuthSession();
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    checkUser();
  }, [token]);

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user } = response.data;
    storeToken(access_token);
    setCachedUser(user);
    setToken(access_token);
    setUser(user);
    return user;
  };

  const demoLogin = async () => {
    const response = await axios.post(`${API}/auth/demo-login`);
    const { access_token, user, is_demo } = response.data;
    storeToken(access_token);
    setCachedUser(user);
    setPreference('is_demo', is_demo ? 'true' : 'false');
    setToken(access_token);
    setUser(user);
    return user;
  };

  const signup = async (email, password, full_name) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, { email, password, full_name });
      
      // Check if email confirmation is required
      if (response.data.email_confirmation_required) {
        // Return the message so the signup page can show it
        throw new Error(response.data.message || 'Please check your email to confirm your account.');
      }
      
      // Normal signup with immediate session
      const { access_token, user } = response.data;
      storeToken(access_token);
      setPreference('needs_onboarding', 'true');
      setToken(access_token);
      setUser(user);
      return user;
    } catch (error) {
      // Extract the actual error message from axios response
      if (error.response?.data?.detail) {
        const err = new Error(error.response.data.detail);
        err.response = error.response; // Preserve the response object
        throw err;
      }
      throw error;
    }
  };

  const logout = async () => {
    // Check demo status before clearing
    const isDemo = getPreference('is_demo');
    clearAuthSession();
    setToken(null);
    setUser(null);
    // Only sign out from Supabase if not a demo user
    if (!isDemo || isDemo !== 'true') {
      await supabase.auth.signOut();
    }
  };

  // Computed value for checking authentication
  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider value={{ user, token, login, signup, demoLogin, logout, updateUser, loading, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};
